import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  TrendingUp, TrendingDown, AlertTriangle, ThumbsUp, MessageSquare,
  DollarSign, Brain, Calendar, Newspaper, Filter, ExternalLink,
} from "lucide-react";

const SOURCE_META = {
  kpi:    { icon: TrendingUp,   label: "KPI",            color: "text-violet-600 bg-violet-50 border-violet-200" },
  revenue:{ icon: DollarSign,   label: "Revenue",        color: "text-blue-600 bg-blue-50 border-blue-200" },
  review: { icon: MessageSquare,label: "Review / MOM",   color: "text-amber-700 bg-amber-50 border-amber-200" },
  intel:  { icon: Newspaper,    label: "Market Intel",   color: "text-slate-700 bg-slate-100 border-slate-200" },
};

const CLASS_META = {
  positive: {
    color: "border-emerald-300 bg-emerald-50",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: ThumbsUp,
    iconColor: "text-emerald-600",
    label: "Positive",
  },
  adverse: {
    color: "border-rose-300 bg-rose-50",
    badge: "bg-rose-100 text-rose-800 border-rose-200",
    icon: AlertTriangle,
    iconColor: "text-rose-600",
    label: "Adverse",
  },
  neutral: {
    color: "border-slate-200 bg-white",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    icon: Calendar,
    iconColor: "text-slate-500",
    label: "Neutral",
  },
};

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function fmtMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y}`;
}

export default function ClientTimeline({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    positive: true,
    adverse: true,
    neutral: true,
    kpi: true,
    revenue: true,
    review: true,
    intel: true,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${clientId}/timeline`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ groups: [], totals: {} }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) {
    return <div className="mptg-card text-center py-12 text-slate-400" data-testid="timeline-loading">Building timeline…</div>;
  }
  if (!data || !data.groups?.length) {
    return (
      <div className="mptg-card text-center py-12" data-testid="timeline-empty">
        <Brain className="w-8 h-8 mx-auto text-slate-300" />
        <div className="mt-3 text-slate-500 font-semibold">No timeline events yet</div>
        <div className="text-xs text-slate-400 mt-1">
          Events will appear as KPIs change, revenue moves, reviews are filed, and market news comes in.
        </div>
      </div>
    );
  }

  const filtered = data.groups.map((g) => ({
    ...g,
    events: g.events.filter(
      (e) => filter[e.classification] && filter[e.source],
    ),
  })).filter((g) => g.events.length > 0);

  const toggle = (k) => setFilter((f) => ({ ...f, [k]: !f[k] }));

  return (
    <div className="space-y-4" data-testid="client-timeline">
      {/* Summary + filters */}
      <div className="mptg-card flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-[#EBF5FB] text-[#2E86C1] flex items-center justify-center">
            <Calendar className="w-4 h-4" />
          </div>
          <div>
            <div className="mptg-overline">Account event timeline</div>
            <div className="text-xs text-slate-500">
              {data.totals.total || 0} events ·{" "}
              <span className="text-emerald-700 font-semibold">{data.totals.positive || 0} positive</span>{" · "}
              <span className="text-rose-700 font-semibold">{data.totals.adverse || 0} adverse</span>
              {data.totals.neutral ? <> · <span className="text-slate-500">{data.totals.neutral} neutral</span></> : null}
            </div>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          {[
            ["positive", "Positive"], ["adverse", "Adverse"], ["neutral", "Neutral"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => toggle(k)}
              className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border transition-colors ${
                filter[k] ? CLASS_META[k].badge : "bg-white text-slate-300 border-slate-200"
              }`}
              data-testid={`tl-filter-${k}`}
            >
              {label}
            </button>
          ))}
          <span className="mx-1 text-slate-300">|</span>
          {[
            ["kpi", "KPI"], ["revenue", "Revenue"], ["review", "MOM"], ["intel", "News"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => toggle(k)}
              className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border transition-colors ${
                filter[k] ? "bg-[#EBF5FB] text-[#2E86C1] border-[#AED6F1]" : "bg-white text-slate-300 border-slate-200"
              }`}
              data-testid={`tl-source-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mptg-card text-center py-10 text-slate-400 text-sm">No events match the current filters.</div>
      ) : (
        <div className="relative pl-6 md:pl-8" data-testid="timeline-stream">
          {/* Vertical spine */}
          <div className="absolute left-2 md:left-3 top-2 bottom-2 w-0.5 bg-slate-200" aria-hidden />
          {filtered.map((g) => (
            <div key={g.month} className="mb-6" data-testid={`tl-month-${g.month}`}>
              {/* Month chip on the spine */}
              <div className="absolute -left-0.5 md:left-0.5 mt-1 w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-[10px] font-bold shadow ring-4 ring-white">
                {parseInt(g.month.split("-")[1], 10)}
              </div>
              <div className="ml-4 md:ml-5 flex flex-wrap items-center gap-2 mb-2">
                <h3 className="font-heading font-bold tracking-tight text-slate-800">
                  {fmtMonth(g.month)}
                </h3>
                {g.counts.positive > 0 && (
                  <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800">
                    +{g.counts.positive}
                  </span>
                )}
                {g.counts.adverse > 0 && (
                  <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-rose-100 text-rose-800">
                    −{g.counts.adverse}
                  </span>
                )}
              </div>
              <div className="ml-4 md:ml-5 space-y-2">
                {g.events.map((e) => <TimelineEvent key={e.id} event={e} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ event }) {
  const cls = CLASS_META[event.classification] || CLASS_META.neutral;
  const src = SOURCE_META[event.source] || SOURCE_META.intel;
  const SrcIcon = src.icon;
  const ClassIcon = cls.icon;
  return (
    <div
      className={`rounded-md border ${cls.color} p-3 hover:shadow-sm transition-shadow`}
      data-testid={`timeline-event-${event.id}`}
    >
      <div className="flex items-start gap-2">
        <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${cls.iconColor}`}>
          <ClassIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 font-bold border ${src.color}`}>
              <SrcIcon className="w-3 h-3" /> {src.label}
            </span>
            <span className={`text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 border ${cls.badge}`}>
              {cls.label}
            </span>
            <span className="text-[10px] text-slate-400 ml-auto">{event.date}</span>
          </div>
          <div className="font-semibold text-slate-800 text-sm mt-1 break-words">{event.title}</div>
          {event.detail && (
            <div className="text-xs text-slate-600 mt-0.5 break-words">{event.detail}</div>
          )}
          {event.link && (
            <a
              href={event.link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#2E86C1] hover:underline mt-1"
              data-testid={`timeline-link-${event.id}`}
            >
              {event.source_name || "Read source"} <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
